/** Página de UM upload — nasceu do painel expandido da lista, quando
 *  ele acumulou métrica + skip + erro + fila de correção e parou de
 *  caber num acordeão. A URL própria também deixa o upload linkável:
 *  colar `/empresas/x/uploads/7` no chat aponta direto pro problema.
 *
 *  A página busca os próprios dados (resumo + linhas com erro +
 *  skips) e faz o próprio polling: enquanto o upload processa E
 *  enquanto houver reprocessamento despachado na fila — os dois estados
 *  em que os números mudam sozinhos. */

import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Info,
  LoaderCircle,
  MessageSquareText,
  RefreshCw,
  SearchX,
  Trash2
} from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  apiClient,
  type CatalogImport,
  type CorrectionGroup,
  type CorrectionSheetSubmission,
  type CorrectionItem,
  type ErrorLine,
  type ErrorLineAttempt,
  type UploadSkipItem,
  type UploadSummary
} from "@/shared/api/api-client";

import { UploadMetrics } from "./UploadMetrics";
import { isImportActive } from "@/shared/utils/catalog-import";
import { usePolling } from "@/shared/hooks/usePolling";
import {
  ACTIVE_STATUSES,
  canDownload,
  downloadBlob,
  progressPercent,
  statusMeta,
  uploadLabel
} from "./upload-shared";

import { ApiError, errorMessage } from "@/shared/api/http";
import { AppLink } from "@/shared/components/AppLink";
import { Pager } from "@/shared/components/Pager";
import { ApiState } from "@/shared/components/ApiState";
import { ExportFeedback, useExportRequest } from "@/features/exports/use-export-request";
import { canonicalFieldLabel } from "@/shared/vocabulary/canonical-fields";
import {
  ERROR_LINE_GROUP_LABELS,
  ERROR_LINE_STATE_LABELS
} from "@/shared/vocabulary/error-line-groups";
import { parseServerTime } from "./upload-metrics";
import { bucketOf, groupKey, isImportGroup } from "./import-group";
import { UseExistingDialog } from "./UseExistingDialog";
import { CorrectionGroupsPanel } from "./CorrectionGroupsPanel";
import { CorrectionSheetDialog } from "./CorrectionSheetDialog";
import { tabOf } from "./correction-group-tabs";
import { SheetSubmissions } from "./SheetSubmissions";
// Seção com até isso de itens nasce aberta; maior, nasce fechada — 155
// linhas de skip abertas empurravam o resto da página pra fora da tela.
// A contagem fica sempre no cabeçalho, então fechada não esconde nada.
const SECTION_OPEN_THRESHOLD = 10;

const SKIP_REASON_LABELS: Record<string, string> = {
  // Só aparece em upload anterior a 2026-09-14: desde então linha sem
  // marca vai pra fila de correção (a pessoa completa o texto e
  // reprocessa), e o rótulo fica pra história não mudar de nome.
  no_brand_match: "nenhum termo de marca bateu",
  already_ingested: "post já existia de um upload anterior",
  // Sem tom de aviso no rótulo nem no estilo: este pulo é uma DECISÃO da
  // empresa ("este canal não entra"), não uma linha que deu problema.
  catalog_ignored: "canal/veículo marcado como ignorado no cadastro"
};

/** Linhas por página das listas do detalhe (linhas com erro e pulos). */
const LIST_PAGE_SIZE = 50;

/** Última página que existe para `total` linhas (sempre >= 1). */
function lastPageOf(total: number): number {
  return Math.max(1, Math.ceil(total / LIST_PAGE_SIZE));
}

/** Sanfona das seções de lista. `defaultOpen` é congelado no PRIMEIRO
 *  render da seção (useState) de propósito: se a lista crescer durante
 *  o processamento, a escolha da pessoa (ou o estado inicial) não é
 *  sobrescrita a cada poll. */
function CollapsibleSection({
  title,
  count,
  icon,
  defaultOpen,
  children
}: {
  title: string;
  count: number;
  icon?: ReactNode;
  defaultOpen: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card">
      <button
        className="collapsible-head"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <h4 className="upload-detail-heading">{icon}{title} · {count}</h4>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </section>
  );
}

/** Melhor esforço pra identificar a linha: a coluna de título muda de
 *  nome por clipadora, então procura por nome parecido e cai pro
 *  primeiro valor não-vazio. */
function skipRowTitle(rawRow: Record<string, string> | null): string | null {
  if (!rawRow) return null;
  for (const [key, value] of Object.entries(rawRow)) {
    if (/t[ií]tulo|title/i.test(key) && value?.trim()) return value;
  }
  const first = Object.values(rawRow).find(value => value?.trim());
  return first ?? null;
}

// ── Fila de correção: edita a linha reprovada campo a campo ──────────

function CorrectionEditor({
  token,
  item,
  textColumns = [],
  onResolved
}: {
  token: string;
  item: CorrectionItem;
  /** As colunas que o template do upload mapeia pro texto da publicação:
   * saem do grid de campos de uma linha e viram caixa grande. */
  textColumns?: string[];
  onResolved: () => void;
}) {
  const [row, setRow] = useState<Record<string, string>>(item.raw_row);
  const [manualContent, setManualContent] = useState(item.manual_content ?? "");
  const [busy, setBusy] = useState<null | "save" | "reprocess" | "discard">(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const act = async (kind: "save" | "reprocess" | "discard") => {
    setBusy(kind);
    setFeedback(null);
    try {
      if (kind === "save") {
        await apiClient.saveCorrection(token, item.id, row, manualContent.trim() || null);
        setFeedback("Linha salva — reprocesse quando estiver pronta.");
      } else if (kind === "reprocess") {
        await apiClient.saveCorrection(token, item.id, row, manualContent.trim() || null);
        await apiClient.reprocessCorrections(token, [item.id]);
        onResolved();
      } else {
        await apiClient.discardCorrection(token, item.id);
        onResolved();
      }
    } catch (error) {
      setFeedback(errorMessage(error, "Não foi possível concluir."));
    } finally {
      setBusy(null);
    }
  };

  // Coluna listada que a linha não tem é ignorada (template mudou, ou a
  // planilha veio sem ela).
  const textKeys = textColumns.filter(column => column in row);
  const setColumn = (key: string, value: string) =>
    setRow(current => ({ ...current, [key]: value }));

  return (
    <div className="correction-editor">
      <div className="correction-fields">
        {Object.entries(row)
          .filter(([key]) => !textKeys.includes(key))
          .map(([key, value]) => (
            <label key={key}>
              <span>{key}</span>
              <input
                value={value ?? ""}
                onChange={event => setColumn(key, event.target.value)}
              />
            </label>
          ))}
      </div>
      {textKeys.map(key => (
        <label key={key} className="correction-text-column">
          <span>Texto da publicação · coluna "{key}"</span>
          <textarea
            rows={6}
            value={row[key] ?? ""}
            onChange={event => setColumn(key, event.target.value)}
          />
          <small>{(row[key] ?? "").length} caracteres</small>
        </label>
      ))}
      {item.collected_content && (
        <details className="correction-collected-content">
          <summary>
            Texto coletado automaticamente ({item.collected_content.length} caracteres)
          </summary>
          <p>
            É o que a coleta trouxe na última tentativa. Se veio incompleto, use como ponto de
            partida e complete no campo abaixo.
          </p>
          <pre>{item.collected_content}</pre>
          <button
            type="button"
            className="secondary-button"
            disabled={manualContent === item.collected_content}
            onClick={() => setManualContent(item.collected_content ?? "")}
          >
            Usar como ponto de partida
          </button>
        </details>
      )}
      <label className="correction-manual-content">
        <span>Conteúdo da matéria (colar manualmente)</span>
        <textarea
          rows={5}
          placeholder={
            textKeys.length > 0
              ? "Opcional: o texto colado aqui SUBSTITUI o texto da planilha mostrado acima. Normalmente basta editar a caixa de cima."
              : "Se a coleta automática do texto falhou (link fora do ar, site mudou), cole aqui o texto da matéria — o reprocessamento usa este texto e pula a coleta."
          }
          value={manualContent}
          onChange={event => setManualContent(event.target.value)}
        />
        {manualContent.trim() && (
          <small>Com texto colado, a coleta automática é pulada nesta linha.</small>
        )}
      </label>
      {feedback && <p className="correction-feedback">{feedback}</p>}
      <div className="correction-actions">
        <button className="secondary-button" disabled={busy !== null} onClick={() => void act("discard")}>
          {busy === "discard" ? <LoaderCircle className="spin" size={15}/> : <Trash2 size={15}/>}Descartar
        </button>
        <button className="secondary-button" disabled={busy !== null} onClick={() => void act("save")}>
          {busy === "save" ? <LoaderCircle className="spin" size={15}/> : <Check size={15}/>}Salvar
        </button>
        <button className="primary-button" disabled={busy !== null} onClick={() => void act("reprocess")}>
          {busy === "reprocess" ? <LoaderCircle className="spin" size={15}/> : <RefreshCw size={15}/>}Salvar e reprocessar
        </button>
      </div>
    </div>
  );
}

// ── Linhas com erro: histórico de tentativas de uma linha ────────────

type AttemptsCache = Map<number, { attempts: number; items: ErrorLineAttempt[] }>;

function formatAttemptTime(value: string): string {
  const parsed = parseServerTime(value);
  return parsed === null ? value : new Date(parsed).toLocaleString("pt-BR");
}

/** O histórico de tentativas de UMA linha, carregado uma vez ao expandir.
 *  O cache mora na página (fechar e reabrir a linha não pede de novo) e
 *  vale enquanto a contagem de tentativas da linha for a mesma — uma
 *  tentativa nova (reprocesso que falhou) o invalida. Falha fica aqui
 *  dentro, com "tentar de novo": nunca derruba a página. */
function AttemptsHistory({
  token,
  uploadId,
  rowIndex,
  attempts,
  cache
}: {
  token: string;
  uploadId: number;
  rowIndex: number;
  attempts: number;
  cache: AttemptsCache;
}) {
  const cached = cache.get(rowIndex);
  const [items, setItems] = useState<ErrorLineAttempt[] | null>(
    cached && cached.attempts === attempts ? cached.items : null
  );
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      const result = await apiClient.listErrorLineAttempts(token, uploadId, rowIndex);
      cache.set(rowIndex, { attempts, items: result });
      setItems(result);
    } catch (error) {
      setFailure(errorMessage(error, "Não foi possível carregar o histórico."));
    }
  }, [token, uploadId, rowIndex, attempts, cache]);

  useEffect(() => {
    if (items === null) void load();
    // Só ao montar: reabrir a linha remonta e lê o cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="error-line-history">
      <h5>Histórico de tentativas</h5>
      {failure && (
        <p className="correction-feedback">
          {failure}{" "}
          <button type="button" className="secondary-button" onClick={() => void load()}>
            tentar de novo
          </button>
        </p>
      )}
      {!failure && items === null && (
        <p className="correction-feedback"><LoaderCircle className="spin" size={13}/> Carregando…</p>
      )}
      {items !== null && (
        <ul className="error-line-attempts">
          {items.map(item => (
            <li key={item.id}>
              <small>{formatAttemptTime(item.at)}</small>
              {item.channel && <span className="upload-error-line-channel">{item.channel}</span>}
              <p>{item.message}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ErrorLineIcon({ line }: { line: ErrorLine }) {
  if (line.state === "queued") return <LoaderCircle className="spin" size={15}/>;
  if (line.state === "reprocessed") return <Check size={15}/>;
  if (line.state === "discarded") return <Trash2 size={15}/>;
  return <AlertTriangle size={15}/>;
}

// ── A página ─────────────────────────────────────────────────────────

type UploadDetailPageProps = {
  token: string;
  uploadId: number;
  /** Pra onde "Voltar" leva. Link, e não callback (2026-10-26): vale
   * "abrir em nova aba". Vem da rota, que conhece a empresa. */
  backHref: string;
  /** A tela de menções já filtrada por ESTE upload (ver
   * `uploadMentionsHref`). */
  mentionsHref: string;
  /** O Ajustes já na aba do cadastro que falta. Vem da rota porque este
   * componente não conhece a empresa. */
  settingsTabHref?: (
    tab: "canais" | "veiculos" | "temas" | "mensagens",
    params?: Record<string, string>
  ) => string;
  /** Leva à página de Downloads, onde a planilha pedida aparece. Vem da
   * rota, como as outras navegações. */
  onOpenDownloads?: () => void;
};

export function UploadDetailPage({
  token,
  uploadId,
  backHref,
  mentionsHref,
  settingsTabHref,
  onOpenDownloads
}: UploadDetailPageProps) {
  // A planilha do upload vai pra fila (2026-10-15): o clique devolve o
  // pedido na hora e o arquivo aparece em Downloads.
  const exporter = useExportRequest({ token, onOpenDownloads });
  const [upload, setUpload] = useState<UploadSummary | null>(null);
  // A tabela "Linhas com erro" (2026-10-30): UMA linha por linha do
  // upload, agrupada por motivo pelo servidor. Substituiu a fila de
  // correção e os erros registrados, que eram duas listas paginadas
  // separadas sobre os mesmos fatos.
  const [errorLines, setErrorLines] = useState<ErrorLine[]>([]);
  const [errorLineTotal, setErrorLineTotal] = useState(0);
  const [errorLinePage, setErrorLinePage] = useState(1);
  // `null` = a pessoa ainda não escolheu: vale o padrão (só pendentes
  // enquanto o resumo diz que há pendência, senão todas).
  const [onlyPendingChoice, setOnlyPendingChoice] = useState<boolean | null>(null);
  const attemptsCache = useRef<AttemptsCache>(new Map());
  // As colunas de texto vêm com a página: são do template do upload, e não
  // mudam entre páginas.
  const [textColumns, setTextColumns] = useState<string[]>([]);
  const [correctionGroups, setCorrectionGroups] = useState<CorrectionGroup[]>([]);
  // A chave do balde em reprocesso (`groupKey`), e não o canal: todo
  // balde de VALOR IMPORTADO tem canal nulo e colidiria com o "sem canal".
  const [reprocessingGroup, setReprocessingGroup] = useState<string | null>(null);
  // "Reprocessar todos os baldes" em voo — um POST por balde, em sequência.
  const [reprocessingAll, setReprocessingAll] = useState(false);
  // O grupo que está no diálogo "usar um existente", e as marcas pra
  // mostrar de qual é cada grupo de tema/mensagem (só carregam quando
  // aparece um grupo de valor importado).
  const [choosing, setChoosing] = useState<CorrectionGroup | null>(null);
  // A planilha de pendências de volta (2026-10-30): o pop-up de envio e a
  // faixa dos envios já feitos, que vem do servidor (o recibo é a página).
  const [sendingSheet, setSendingSheet] = useState(false);
  const [submissions, setSubmissions] = useState<CorrectionSheetSubmission[]>([]);
  const [brandNames, setBrandNames] = useState<Record<number, string>>({});
  // Uma importação de catálogo em andamento na empresa. Enquanto ela
  // roda, "Reprocessar todas" fica desabilitado: as linhas do balde
  // seriam reavaliadas contra um catálogo que ainda está mudando, e
  // metade delas voltaria pra fila.
  const [activeImport, setActiveImport] = useState<CatalogImport | null>(null);
  const [skips, setSkips] = useState<UploadSkipItem[]>([]);
  const [skipTotal, setSkipTotal] = useState(0);
  const [skipPage, setSkipPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [openItem, setOpenItem] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<null | "pending">(null);
  // Falha de AÇÃO (baixar, reprocessar um grupo), separada do `failure`
  // de CARREGAMENTO: aquele troca a página inteira por um aviso, que é
  // certo quando o upload não pôde ser lido e destrutivo quando o
  // download é que falhou — a pessoa perderia a tela que estava usando.
  const [actionFailure, setActionFailure] = useState<string | null>(null);
  // Até quando o servidor pediu pra não perguntar nada (429). Ver
  // `usePolling`.
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  // O `upload` que o `catch` de `loadSummary` consulta, por REF e não
  // por dependência. Com `upload` nas deps do `useCallback`, cada
  // resposta (um objeto novo) recriava a função, o efeito de carga
  // inicial — que depende dela — rodava de novo, e a página batia em
  // `GET /uploads/{id}` centenas de vezes por segundo até o 429 a
  // parar (medido no devstack em 2026-09-14: 662 requisições numa
  // abertura). Estava assim desde 2026-09-10, quando o `catch` passou
  // a distinguir "sem upload" de "upload em mãos".
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  // O TICK busca só o resumo. Ele é pequeno (~400 B) e, mais importante,
  // já CARREGA os contadores que dizem se cada lista mudou — buscar as
  // três a cada 2,5 s eram 4 requisições por tick (~628 num upload de
  // 393 s) para redesenhar dado idêntico, e as listas ainda piscavam.
  const loadSummary = useCallback(async () => {
    try {
      setUpload(await apiClient.uploadSummary(token, uploadId));
      setFailure(null);
      setActionFailure(null);
      setPausedUntil(null);
    } catch (error) {
      if (error instanceof ApiError && error.retryAfterSeconds !== null) {
        // Pausa o polling até o servidor liberar. Sem isso o tick
        // continua batendo — o backoff por assinatura só sobe depois de
        // 4 batidas e tem teto de 10 s, então contra um `Retry-After`
        // de 60 s são ~8 batidas inúteis por minuto, cada uma custando
        // outro 429.
        setPausedUntil(Date.now() + error.retryAfterSeconds * 1000);
      }
      // **Recarga que falha NÃO troca a página.** A distinção já estava
      // escrita aqui pro `actionFailure` e valia só metade: com o
      // upload já em mãos, substituir a tela inteira por "não foi
      // possível carregar" tira da pessoa o que ela estava lendo por
      // causa de um tick ruim — e o próximo tick bom o traz de volta,
      // então o sintoma era a tela PISCAR. Sem o upload, aí sim o aviso
      // é a página: não há o que mostrar.
      const texto = errorMessage(
        error,
        "Não foi possível carregar o upload."
      );
      if (uploadRef.current === null) setFailure(texto);
      else setActionFailure(texto);
    } finally {
      setLoading(false);
    }
  }, [token, uploadId]);

  // A lista pede UMA página (`LIST_PAGE_SIZE`) e guarda o total do
  // servidor. A página e o filtro moram no `useCallback` — trocá-los
  // recria o carregador e o efeito dele busca, que é a "carga explícita"
  // de quem clicou no Pager. O contador por ref descarta a resposta de
  // uma carga que já não é a pedida (clicar "próxima" duas vezes rápido).
  // E página além do total (resolveu as da última) volta pra última
  // válida SEM gravar a resposta vazia: o `set*Page` recria o carregador
  // e o efeito refaz.
  const onlyPending = onlyPendingChoice ?? (upload?.pending_correction_count ?? 0) > 0;
  const errorLineLoadRef = useRef(0);
  const loadErrorLines = useCallback(async () => {
    const load = ++errorLineLoadRef.current;
    try {
      const result = await apiClient.listErrorLines(token, uploadId, {
        page: errorLinePage,
        size: LIST_PAGE_SIZE,
        pendingOnly: onlyPending
      });
      if (load !== errorLineLoadRef.current) return;
      const last = lastPageOf(result.total);
      if (errorLinePage > last) {
        setErrorLinePage(last);
        return;
      }
      setErrorLines(result.items);
      setErrorLineTotal(result.total);
      setTextColumns(result.text_columns ?? []);
    } catch { /* o resumo é quem reporta erro de tela */ }
  }, [token, uploadId, errorLinePage, onlyPending]);

  // Carregada UMA vez ao abrir e de novo depois de um envio — sem
  // polling próprio: o envio só muda por ato de alguém.
  const loadSubmissions = useCallback(async () => {
    try {
      setSubmissions(await apiClient.listCorrectionSheetSubmissions(token, uploadId));
    } catch { /* a faixa é informação extra: falhar não tira a página */ }
  }, [token, uploadId]);

  const loadCorrectionGroups = useCallback(async () => {
    try {
      setCorrectionGroups(await apiClient.uploadCorrectionGroups(token, uploadId));
    } catch { /* idem */ }
  }, [token, uploadId]);

  // Só as últimas: a trava do banco garante UMA ativa por empresa, então
  // a primeira página basta pra achá-la.
  const loadActiveImport = useCallback(async () => {
    try {
      const page = await apiClient.listCatalogImports(token, { size: 5 });
      setActiveImport(page.items.find(isImportActive) ?? null);
    } catch { /* idem */ }
  }, [token]);

  const reprocessGroup = useCallback(
    async (group: CorrectionGroup, options: { rethrow?: boolean } = {}) => {
      setReprocessingGroup(groupKey(group));
      setActionFailure(null);
      try {
        await apiClient.reprocessCorrectionGroup(token, uploadId, bucketOf(group));
        await Promise.all([loadSummary(), loadCorrectionGroups()]);
      } catch (error) {
        setActionFailure(
          error instanceof Error
            ? error.message
            : "Não foi possível reprocessar o grupo."
        );
        // O diálogo "usar um existente" encadeia guardar + reprocessar e
        // precisa saber que o segundo passo falhou.
        if (options.rethrow) throw error;
      } finally {
        setReprocessingGroup(null);
      }
    },
    [token, uploadId, loadSummary, loadCorrectionGroups]
  );

  // Os baldes de uma aba que ainda não estão na fila (o painel escolhe
  // quais), um POST por balde e na ordem da lista (cada POST publica UMA
  // mensagem com o balde inteiro, como o botão individual). Um balde que
  // falha não para os outros; o aviso diz quantos ficaram. Resumo e
  // grupos são recarregados UMA vez no fim, não por balde.
  const reprocessGroups = useCallback(async (idle: CorrectionGroup[]) => {
    if (idle.length === 0) return;
    setReprocessingAll(true);
    setActionFailure(null);
    const failures: string[] = [];
    try {
      for (const group of idle) {
        try {
          await apiClient.reprocessCorrectionGroup(token, uploadId, bucketOf(group));
        } catch (error) {
          failures.push(error instanceof Error ? error.message : "falha desconhecida");
        }
      }
      // A recarga vem ANTES e o aviso DEPOIS (regra de 2026-10-09): o
      // `loadSummary` que dá certo zera o `actionFailure`, e na ordem
      // inversa o aviso sumia antes de a pessoa ler.
      await Promise.all([loadSummary(), loadCorrectionGroups()]);
      if (failures.length > 0) {
        setActionFailure(
          `${failures.length} de ${idle.length} balde(s) não foram mandados pra fila: ${failures[0]}`
        );
      }
    } finally {
      setReprocessingAll(false);
    }
  }, [token, uploadId, loadSummary, loadCorrectionGroups]);

  const skipLoadRef = useRef(0);
  const loadSkips = useCallback(async () => {
    const load = ++skipLoadRef.current;
    try {
      const result = await apiClient.uploadSkips(token, uploadId, {
        page: skipPage,
        size: LIST_PAGE_SIZE
      });
      if (load !== skipLoadRef.current) return;
      const last = lastPageOf(result.total);
      if (skipPage > last) {
        setSkipPage(last);
        return;
      }
      setSkips(result.items);
      setSkipTotal(result.total);
    } catch { /* idem */ }
  }, [token, uploadId, skipPage]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  // Só com o upload em mãos (sem ele nenhuma outra lista é pedida).
  const hasUpload = upload !== null;
  useEffect(() => {
    if (hasUpload) void loadSubmissions();
  }, [hasUpload, loadSubmissions]);

  const needsBrandNames = correctionGroups.some(
    group => isImportGroup(group) && group.import_brand_id !== null
  );
  useEffect(() => {
    if (!needsBrandNames) return;
    let cancelled = false;
    apiClient
      .listBrands(token)
      .then(brands => {
        if (!cancelled) {
          setBrandNames(Object.fromEntries(brands.map(brand => [brand.id, brand.name])));
        }
      })
      .catch(() => { /* o título cai pro texto sem a marca */ });
    return () => {
      cancelled = true;
    };
  }, [needsBrandNames, token]);

  const active = upload !== null && ACTIVE_STATUSES.has(upload.status);
  // Pelo RESUMO, não só pela página de correções carregada: a fila é
  // paginada, e um balde mandado reprocessar pode estar inteiro fora
  // da página 1 — só pela página a tela pararia de acompanhar o
  // reprocesso. A página entra em OR porque ela chega antes do resumo
  // no clique de um item só.
  const hasDispatched =
    (upload?.dispatched_correction_count ?? 0) > 0 ||
    errorLines.some(line => line.state === "queued");

  // A lista de linhas com erro cresce AO VIVO de propósito — é decisão
  // registrada no docstring de `UploadErrorLog`: a tela deve mostrar o
  // erro aparecendo enquanto o resto do arquivo ainda processa. As duas
  // contagens do resumo cobrem o que muda sozinho: erro novo e
  // pendência que entrou ou saiu.
  useEffect(() => {
    if (upload) void loadErrorLines();
  }, [
    upload?.error_count,
    upload?.pending_correction_count,
    hasDispatched,
    loadErrorLines
  ]);

  useEffect(() => {
    if (upload) void loadCorrectionGroups();
  }, [upload?.pending_correction_count, hasDispatched, loadCorrectionGroups]);

  // Só quando há balde de catálogo: numa tela sem essa pendência a
  // pergunta não muda nada na interface.
  const hasCatalogGroup = correctionGroups.some(group => group.catalog_kind);
  // Linha em que nenhum termo de marca casou: a planilha de pendências
  // traz uma aba pra ela. Separado de `hasCatalogGroup`, que também
  // dispara a busca da importação ativa — e essa só faz sentido pra
  // cadastro.
  // A planilha de pendências também lista as linhas que se corrigem uma a
  // uma (sem duração, falha na coleta, sem termo de marca, outros): só o
  // valor da planilha (balde "imported") fica sem nada pra baixar.
  const hasLineLevelGroup = correctionGroups.some(group => tabOf(group) !== "imported" && tabOf(group) !== "catalog");
  useEffect(() => {
    if (hasCatalogGroup) void loadActiveImport();
  }, [hasCatalogGroup, loadActiveImport]);

  // Skips NÃO são rebuscados enquanto processa: o número já aparece ao
  // vivo na faixa de métricas (vem do resumo), a seção nasce fechada
  // acima de 10 itens, e é a maior resposta das três (~5,5 KB). Congela
  // a assinatura enquanto ativo e busca uma vez quando termina.
  const skipSignature = active
    ? "processando"
    : [
        upload?.no_brand_match_count ?? "",
        upload?.already_ingested_count ?? "",
        upload?.catalog_ignored_count ?? ""
      ].join("|");
  useEffect(() => {
    if (upload) void loadSkips();
  }, [skipSignature, loadSkips]);

  // Enquanto há correção despachada o polling continua mesmo com o
  // upload FINISHED — e aí nenhum contador muda, por isso `hasDispatched`
  // entra na assinatura do backoff.
  // O import ativo também mantém o poll de pé: é ele que libera o
  // "Reprocessar todas" quando a importação termina, sem a pessoa
  // precisar recarregar a página.
  usePolling({
    enabled: active || hasDispatched || activeImport !== null,
    onTick: () => {
      void loadSummary();
      if (hasCatalogGroup) void loadActiveImport();
    },
    signature: [
      upload?.status ?? "",
      upload?.processed_rows ?? "",
      hasDispatched,
      activeImport?.pending_count ?? ""
    ].join("|"),
    pausedUntil
  });

  /** Baixa a planilha de pendências de cadastro e reporta a falha na tela.
   *
   *  O `catch` não é zelo: sem ele, uma sessão expirada ou um 404 no
   *  meio do download deixavam o botão voltar ao normal como se nada
   *  tivesse acontecido, e a pessoa ficava esperando um arquivo que
   *  nunca ia chegar. */
  const downloadAs = async (
    kind: "pending",
    fetch: () => Promise<Blob>,
    filename: string
  ) => {
    setDownloading(kind);
    setActionFailure(null);
    try {
      downloadBlob(await fetch(), filename);
    } catch (error) {
      setActionFailure(
        error instanceof Error
          ? error.message
          : "Não foi possível baixar a planilha."
      );
    } finally {
      setDownloading(null);
    }
  };

  // A seção fica enquanto houver linha OU a pessoa tiver escolhido um
  // filtro: sem a segunda, "Só pendentes" vazio sumiria com a seção e o
  // botão "Todas" junto.
  const showErrorLines =
    errorLineTotal > 0 || onlyPendingChoice !== null || submissions.length > 0;
  const changeFilter = (value: boolean) => {
    setOnlyPendingChoice(value);
    setErrorLinePage(1);
    setOpenItem(null);
  };

  if (loading) {
    return (
      <div className="upload-detail-page">
        <div className="upload-detail-loading"><LoaderCircle className="spin" size={16}/>Carregando upload…</div>
      </div>
    );
  }

  if (failure || upload === null) {
    return (
      <div className="upload-detail-page">
        <AppLink className="secondary-button" to={backHref}><ArrowLeft size={15}/>Voltar</AppLink>
        <ApiState kind="error">{failure ?? "Upload não encontrado."}</ApiState>
      </div>
    );
  }

  const meta = statusMeta(upload.status, upload.is_advancing);
  const now = Date.now();

  return (
    <div className="upload-detail-page">
      <header className="upload-detail-head">
        <AppLink className="secondary-button" to={backHref}><ArrowLeft size={15}/>Uploads</AppLink>
        <div className="upload-detail-title">
          <h2>
            {active ? <LoaderCircle className="spin" size={18}/> : <FileSpreadsheet size={18}/>}
            {uploadLabel(upload)}
          </h2>
          {upload.filename && (
            <span className="upload-detail-subtitle">
              #{upload.id} · {upload.clipping_service ?? "sem modelo"}
            </span>
          )}
          <span className={`upload-status ${meta.tone}`}>{meta.label}</span>
          {(upload.imported_dimensions ?? []).length > 0 && (
            <span
              className="upload-imported-note"
              title="Essas dimensões vieram das colunas da planilha, sem passar pela IA"
            >
              Classificação da planilha:{" "}
              {(upload.imported_dimensions ?? [])
                .map(dimension => canonicalFieldLabel(dimension).toLowerCase())
                .join(", ")}
            </span>
          )}
        </div>
        {/* Só com menção pra mostrar: upload zerado abriria a tela de
            menções vazia — o card de métricas já explica o zero. */}
        {upload.mention_count > 0 && (
          <AppLink className="primary-button" to={mentionsHref}>
            <MessageSquareText size={15}/>
            Ver menções ({upload.mention_count})
          </AppLink>
        )}
        {canDownload(upload) && (
          <button
            className="secondary-button"
            title="Baixa os DADOS enviados, reconstruídos — não o arquivo original byte a byte"
            disabled={exporter.busy}
            onClick={() => {
              exporter.clearError();
              void exporter.request({
                kind: "export_upload_rows",
                subject_id: upload.id,
                format: "xlsx"
              });
            }}
          >
            {exporter.busy
              ? <LoaderCircle className="spin" size={15}/>
              : <Download size={15}/>}
            {exporter.busy ? "Gerando…" : "Baixar planilha"}
          </button>
        )}
      </header>

      {actionFailure && (
        <ApiState kind="error">{actionFailure}</ApiState>
      )}
      <ExportFeedback exporter={exporter} />

      {active && (
        <div className="upload-detail-progress">
          <span className="upload-progress-track">
            <span
              className={`upload-progress-fill ${meta.tone}`}
              style={{ width: `${progressPercent(upload)}%` }}
            />
          </span>
          <span>{upload.processed_rows}/{upload.total_rows ?? "?"} linhas</span>
        </div>
      )}

      <section className="card">
        <UploadMetrics upload={upload} now={now}/>
      </section>

      {skipTotal > 0 && (
        <CollapsibleSection
          title="Linhas sem menção"
          count={skipTotal}
          icon={<SearchX size={15}/>}
          defaultOpen={skipTotal <= SECTION_OPEN_THRESHOLD}
        >
          <p className="upload-detail-hint">
            Processaram sem erro, mas não geraram menção — não é defeito da
            linha. "Já existia" é reenvio de post que outro upload trouxe
            primeiro; "ignorado" é a própria empresa tendo marcado aquele
            canal/veículo pra não entrar, e não pede nada. "Nenhum termo
            bateu" só aparece em upload antigo: hoje essa linha vai pra
            fila de correção, abaixo.
          </p>
          <ul className="upload-skip-lines">
            {skips.map(skip => (
              <li key={skip.id}>
                <span className="upload-error-line-index">Linha {skip.row_index + 1}</span>
                {skip.channel && <span className="upload-error-line-channel">{skip.channel}</span>}
                <span className={`upload-skip-reason ${skip.reason}`}>
                  {SKIP_REASON_LABELS[skip.reason] ?? skip.reason}
                </span>
                {skipRowTitle(skip.raw_row) && <p>{skipRowTitle(skip.raw_row)}</p>}
              </li>
            ))}
          </ul>
          <Pager
            page={skipPage}
            size={LIST_PAGE_SIZE}
            count={{ total: skipTotal }}
            onPage={setSkipPage}
          />
        </CollapsibleSection>
      )}

      {/* Os grupos vêm ANTES da lista, e o motivo é a escala: com um
          veículo não cadastrado, "a fila" são milhares de linhas com a
          MESMA causa e a mesma solução. A lista continua embaixo pra
          quem precisa editar linha a linha. */}
      {correctionGroups.length > 0 && (
        <CorrectionGroupsPanel
          groups={correctionGroups}
          brandNames={brandNames}
          postSource={upload.post_source ?? null}
          activeImport={activeImport}
          reprocessingGroup={reprocessingGroup}
          reprocessingMany={reprocessingAll}
          showDownload={hasCatalogGroup || hasLineLevelGroup}
          downloadingPending={downloading === "pending"}
          onDownloadPending={() =>
            void downloadAs(
              "pending",
              () => apiClient.downloadPendingCatalog(token, upload.id),
              `pendencias-upload-${upload.id}.xlsx`
            )
          }
          onSendSheet={() => setSendingSheet(true)}
          onReprocess={group => void reprocessGroup(group)}
          onReprocessMany={idle => void reprocessGroups(idle)}
          onChoose={setChoosing}
          settingsTabHref={settingsTabHref}
        />
      )}

      {sendingSheet && (
        <CorrectionSheetDialog
          token={token}
          uploadId={upload.id}
          onClose={() => setSendingSheet(false)}
          // A recarga vem ANTES de o pop-up fechar (regra de 2026-10-09):
          // quando a pessoa volta pra página, ela já está nova. A faixa é
          // o aviso — não há toast.
          onSubmitted={async () => {
            await Promise.all([
              loadSummary(),
              loadCorrectionGroups(),
              loadErrorLines(),
              loadSubmissions()
            ]);
          }}
        />
      )}

      {choosing && (
        <UseExistingDialog
          token={token}
          uploadId={upload.id}
          group={choosing}
          brandName={
            choosing.import_brand_id !== null && Object.keys(brandNames).length > 1
              ? brandNames[choosing.import_brand_id]
              : null
          }
          postSource={upload.post_source ?? null}
          reprocess={group => reprocessGroup(group, { rethrow: true })}
          onClose={() => setChoosing(null)}
          onDone={() => setChoosing(null)}
        />
      )}

      {showErrorLines && (
        <CollapsibleSection
          title="Linhas com erro"
          count={errorLineTotal}
          defaultOpen={errorLineTotal <= SECTION_OPEN_THRESHOLD}
        >
          <p className="upload-detail-hint">
            Cada linha do arquivo aparece uma vez, com o erro mais recente; as
            tentativas anteriores estão no histórico dela. Edite a linha reprovada
            e reprocesse — o conjunto de colunas precisa continuar o mesmo do
            arquivo original. Cadastro faltando resolve o grupo inteiro de uma
            vez, acima.
          </p>
          <SheetSubmissions submissions={submissions}/>
          <div className="mode-toggle correction-tabs" role="group" aria-label="Quais linhas listar">
            <button
              type="button"
              aria-pressed={onlyPending}
              className={onlyPending ? "active" : ""}
              onClick={() => changeFilter(true)}
            >
              Só pendentes
            </button>
            <button
              type="button"
              aria-pressed={!onlyPending}
              className={!onlyPending ? "active" : ""}
              onClick={() => changeFilter(false)}
            >
              Todas
            </button>
          </div>
          {errorLines.length === 0 && (
            <div className="inline-empty">
              {onlyPending ? "Nenhuma linha pendente." : "Nenhuma linha com erro."}
            </div>
          )}
          <div className="correction-list">
            {errorLines.map((line, index) => {
              const rowIndex = line.row_index;
              const open = rowIndex !== null && openItem === rowIndex;
              const stateLabel = ERROR_LINE_STATE_LABELS[line.state];
              const startsGroup = index === 0 || errorLines[index - 1].group !== line.group;
              const head = (
                <>
                  <ErrorLineIcon line={line}/>
                  <strong>{rowIndex !== null ? `Linha ${rowIndex + 1}` : "Arquivo"}</strong>
                  {line.channel && <span className="error-line-channel">{line.channel}</span>}
                  {/* A mensagem é cortada em uma linha; o `title` dá o texto
                      inteiro sem expandir — e o erro de arquivo não expande. */}
                  <span className="error-line-message" title={line.message}>{line.message}</span>
                  {stateLabel && (
                    <span className={`error-line-chip ${line.state}`}>{stateLabel}</span>
                  )}
                  {line.attempts > 1 && (
                    <span className="error-line-chip">{line.attempts} tentativas</span>
                  )}
                  {rowIndex !== null && (open ? <ChevronUp size={15}/> : <ChevronDown size={15}/>)}
                </>
              );
              return (
                <Fragment key={rowIndex !== null ? `row-${rowIndex}` : `file-${index}`}>
                  {startsGroup && (
                    <h5 className="error-line-group-title">
                      {ERROR_LINE_GROUP_LABELS[line.group]}
                    </h5>
                  )}
                  <article>
                    {rowIndex !== null ? (
                      <button
                        className="correction-row"
                        aria-expanded={open}
                        onClick={() => setOpenItem(current => (current === rowIndex ? null : rowIndex))}
                      >
                        {head}
                      </button>
                    ) : (
                      <div className="correction-row">{head}</div>
                    )}
                    {open && line.state === "queued" && (
                      // Sem editor nem botões: o worker vai pegar este item a
                      // qualquer momento, e edição agora seria corrida contra
                      // ele (salvaria uma linha que talvez já foi processada).
                      <p className="correction-queued-notice">
                        Reprocessamento na fila — a esteira termina o que está na
                        frente (inclusive uploads inteiros) e chega nele em breve.
                        A linha some daqui sozinha quando terminar.
                      </p>
                    )}
                    {open && line.state === "pending" && line.correction && (
                      <CorrectionEditor
                        key={line.correction.id}
                        token={token}
                        item={line.correction}
                        textColumns={textColumns}
                        // Ação do usuário busca as DUAS coisas na hora: a
                        // lista some/atualiza, e o resumo recalcula os
                        // contadores. Aqui não dá pra esperar tick.
                        onResolved={() => {
                          setOpenItem(null);
                          void loadSummary();
                          void loadErrorLines();
                        }}
                      />
                    )}
                    {open && rowIndex !== null && (
                      <AttemptsHistory
                        token={token}
                        uploadId={upload.id}
                        rowIndex={rowIndex}
                        attempts={line.attempts}
                        cache={attemptsCache.current}
                      />
                    )}
                  </article>
                </Fragment>
              );
            })}
          </div>
          <Pager
            page={errorLinePage}
            size={LIST_PAGE_SIZE}
            count={{ total: errorLineTotal }}
            onPage={page => {
              setOpenItem(null);
              setErrorLinePage(page);
            }}
          />
        </CollapsibleSection>
      )}

      {/* "Nenhuma pendência", e NÃO "todas viraram menção": upload
          anterior ao registro de skips pode ter gap sem nada listado
          aqui, e afirmar mais do que o registro cobre seria mentira. */}
      {!showErrorLines && skipTotal === 0 && !active && (
        <div className="inline-empty">Nenhuma pendência registrada para este upload.</div>
      )}

      <p className="upload-detail-hint">
        <Info size={13}/> A planilha devolve os dados enviados reconstruídos
        (tudo como texto) — não o arquivo original byte a byte. Ela é gerada
        em segundo plano e fica na página Downloads.
      </p>
    </div>
  );
}
